<template>
  <div v-if="isLoading || hasError || (auditResults && auditResults.length > 0)" class="review-results">
    <div class="review-header">
      <h2 class="review-title">{{ title }}</h2>
      <button
        class="close-button"
        @click="close"
        aria-label="Close">
        <span class="material-icons-round">close</span>
      </button>
    </div>

    <div class="audit-items">
      <div
        v-if="shouldShowLoading"
        class="loading-state"
        role="status"
        aria-live="polite"
      >
        <i class="fa fa-circle-o-notch fa-spin" />
        <span class="loading-text">{{ loadingText }}</span>
      </div>

      <div
        v-else-if="hasError"
        class="error-state"
        role="alert"
        aria-live="assertive"
      >
        <span class="material-icons-round error-icon" aria-hidden="true">warning_amber</span>
        <span class="error-text">{{ errorMessage }}</span>
      </div>

      <template v-if="sortedAuditResults && sortedAuditResults.length > 0">
        <div
          v-for="(result, index) in sortedAuditResults"
          :key="index"
          class="audit-item"
        >
          <button
            class="audit-item-header"
            @click="toggleItem(index)"
            :aria-expanded="expandedItems[index] ? 'true' : 'false'"
          >
            <div class="audit-item-title">
              <div class="score-badge" :class="getScoreBadgeClass(result.score, result.max_score)">
                {{ formatScore(result.score, result.max_score) }}
              </div>
              <span class="audit-title">{{ result.audit_title }}</span>
            </div>
            <span class="material-icons-round chevron">
              {{ expandedItems[index] ? 'expand_less' : 'expand_more' }}
            </span>
          </button>

          <transition name="collapse">
            <div v-if="expandedItems[index]" class="audit-item-content">
              <div class="audit-section">
                <h4 class="section-label">Rationale</h4>
                <p class="section-text">{{ result.score_rationale }}</p>
              </div>

              <div v-if="result.suggestions" class="audit-section">
                <h4 class="section-label">Suggestions for Improvement</h4>
                <p class="section-text">{{ result.suggestions }}</p>
              </div>
            </div>
          </transition>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReviewResults',
  props: {
    title: {
      type: String,
      default: 'Review'
    },
    auditResults: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    criteriaItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    hasError() {
      return !!this.errorMessage
    },
    loadingText() {
      return this.title === 'Goal Review' ? 'Reviewing Goal...' : 'Reviewing Objectives...'
    },
    expectedCriteriaCount() {
      return this.criteriaItems.length
    },
    sortedAuditResults() {
      if (!this.auditResults || this.auditResults.length === 0) {
        return []
      }

      // Sort results by the order they appear in criteriaItems
      // Items not found in criteriaItems are sorted to the end
      const arbitraryLargeNumber = 9999;
      return [...this.auditResults].sort((a, b) => {
        const indexA = this.criteriaItems.findIndex(c => c.label === a.audit_title)
        const indexB = this.criteriaItems.findIndex(c => c.label === b.audit_title)
        const orderA = indexA === -1 ? arbitraryLargeNumber : indexA
        const orderB = indexB === -1 ? arbitraryLargeNumber : indexB
        return orderA - orderB
      })
    },
    shouldShowLoading() {
      if (!this.isLoading) return false

      const receivedCount = this.auditResults ? this.auditResults.length : 0

      // If criteriaItems is provided, use it to determine if we should show loading
      if (this.criteriaItems.length > 0) {
        // Show loading only if we haven't received all expected results yet
        return receivedCount < this.expectedCriteriaCount
      }

      // If no criteriaItems provided, show loading as long as we have no results
      return receivedCount === 0
    }
  },
  data() {
    return {
      expandedItems: {}
    }
  },
  watch: {
    sortedAuditResults: {
      immediate: true,
      handler(newResults) {
        if (newResults && newResults.length > 0) {
          const expanded = {}
          newResults.forEach((_, index) => {
            expanded[index] = false
          })
          this.expandedItems = expanded
        }
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    toggleItem(index) {
      this.$set(this.expandedItems, index, !this.expandedItems[index])
      const thisResult = this.sortedAuditResults[index]
      this.$sendEvent('Clicked Goal Review Results Item Toggle', {
        section: thisResult?.section,
        criteria_title: thisResult?.audit_title,
        score: thisResult?.score,
        max_score: thisResult?.max_score
      })
    },
    formatScore(score, maxScore) {
      if (isNaN(score) || score === null || score === undefined) {
        return 'N/A'
      }
      return `${score}/${maxScore}`
    },
    getScoreBadgeClass(score, maxScore) {
      if (!maxScore) {
        return 'score-low'
      }
      const percentage = (score / maxScore) * 100

      if (percentage >= 80) {
        return 'score-high'
      } else if (percentage >= 50) {
        return 'score-medium'
      } else {
        return 'score-low'
      }
    }
  }
}
</script>

<style scoped lang="less">
.review-results {
  border: 2px solid transparent;
  border-radius: 8px;
  background:
    linear-gradient(#FFFFFF, #FFFFFF) padding-box,
    var(--AI-AI-Blue-Pink) border-box;
  font-weight: 400;
}

.review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 12px 24px;
  border-bottom: 1px solid #ccc;
}

.review-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
  color: #007dba;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  width: 24px;
  height: 24px;
  &:hover {
    color: #000;
  }

  .material-icons-round {
    font-size: 24px;
  }
}

.audit-items {
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading-text {
  font-size: 14px;
}

.loading-state,
.error-state {
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-state {
  .error-icon {
    font-size: 24px;
    color: #991b1b;
  }

  .error-text {
    font-size: 14px;
  }
}

.audit-item {
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  padding: 1px;
}

.audit-item-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border: none;
  cursor: pointer;

  &:hover {
    background: #e6e6e6;
  }
}

.audit-item-title {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.score-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: white;
  min-width: 53px;

  &.score-high {
    background: #136320;
  }

  &.score-medium {
    background: #A16207;
  }

  &.score-low {
    background: #991b1b;
  }
}

.audit-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.chevron {
  color: #6f6f6f;
}

.audit-item-content {
  padding: 0 16px 16px 16px;
  border-top: 1px solid #e6e6e6;
}

.audit-section {
  margin-top: 16px;
}

.section-label {
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 8px 0;
}

.section-text {
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  white-space: pre-wrap;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.collapse-enter,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
